/**
 * Shared API validation helpers — map express-validator errors to forms & toasts.
 */

const GENERIC_MESSAGES = /^(validation failed|request failed|bad request|unprocessable entity|invalid value)$/i;

/**
 * Map backend validation errors array → { field: message } for inline form display.
 * Stores both full path (`sports.position`) and leaf key (`position`) when safe.
 */
export function mapApiValidationErrors(errors) {
  if (!errors) return {};
  if (!Array.isArray(errors)) {
    if (typeof errors === 'object') {
      const mapped = {};
      Object.entries(errors).forEach(([key, value]) => {
        if (value == null) return;
        mapped[key] = Array.isArray(value) ? value[0]?.msg || value[0] : value;
      });
      return mapped;
    }
    return {};
  }

  const fieldErrors = {};

  errors.forEach((entry) => {
    const msg = entry?.msg || entry?.message;
    if (!msg) return;

    const path = entry.path || entry.param;
    if (!path) return;

    const key = String(path);
    if (!fieldErrors[key]) fieldErrors[key] = msg;

    const parts = key.split('.');
    const leaf = parts[parts.length - 1];
    const hasIndex = parts.some((p) => /^\d+$/.test(p));

    // Safe leaf mapping: sports.position → position (not grounds.0.name → name)
    if (!hasIndex && parts.length <= 2 && leaf && !fieldErrors[leaf]) {
      fieldErrors[leaf] = msg;
    }
  });

  return fieldErrors;
}

/** First human-readable validation message from an errors payload. */
export function getFirstValidationMessage(errors) {
  if (!errors) return null;
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) {
    return errors[0]?.msg || errors[0]?.message || (typeof errors[0] === 'string' ? errors[0] : null);
  }
  if (typeof errors === 'object') {
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) return null;
    const val = errors[firstKey];
    if (Array.isArray(val)) return val[0]?.msg || val[0] || null;
    return val || null;
  }
  return null;
}

/**
 * Prefer field-specific validation text over generic API messages.
 * @param {object} err - Normalized API error `{ message, errors, status }`
 * @param {string} fallback
 */
export function getApiErrorMessage(err, fallback = 'Something went wrong') {
  const fromErrors = getFirstValidationMessage(err?.errors);
  const message = err?.message;

  if (fromErrors && (!message || GENERIC_MESSAGES.test(String(message)))) {
    return fromErrors;
  }
  if (fromErrors && message === 'Validation failed') {
    return fromErrors;
  }
  return message || fromErrors || fallback;
}

/**
 * Apply API validation errors to a form; returns true if any field errors were set.
 */
export function applyApiValidationErrors(err, setErrors) {
  const fieldErrors = mapApiValidationErrors(err?.errors);
  if (Object.keys(fieldErrors).length) {
    setErrors(fieldErrors);
    return true;
  }
  return false;
}
